import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  HourglassTopOutlined,
  PhotoLibraryOutlined,
  VerifiedUser,
  HomeOutlined,
} from "@mui/icons-material";
import { DASH } from "../../dashboard/tokens";

export type HeaderClaimState = "owner" | "pending" | "claimed" | "unclaimed";

type Props = {
  line1: string;
  line2: string;
  /** Background images in preference order; the next is tried if one fails to load. */
  imageCandidates: string[];
  photoCount: number;
  onViewPhotos: () => void;
  claimState: HeaderClaimState;
  onClaim: () => void;
  /** Wishlist heart for non-owners; omit to hide. */
  wishlist?: { active: boolean; onToggle: () => void } | null;
  /** Right side of the header on desktop (anniversary card / claim card). */
  aside?: React.ReactNode;
};

const BADGES: Record<HeaderClaimState, { label: string; icon: React.ReactNode; bg: string }> = {
  owner: { label: "Home claimed & protected", icon: <VerifiedUser sx={{ fontSize: 16 }} />, bg: "rgba(46,125,50,0.9)" },
  claimed: { label: "Claimed & protected", icon: <VerifiedUser sx={{ fontSize: 16 }} />, bg: "rgba(46,125,50,0.9)" },
  pending: { label: "Claim under review", icon: <HourglassTopOutlined sx={{ fontSize: 16 }} />, bg: "rgba(178,106,0,0.92)" },
  unclaimed: { label: "Not yet claimed", icon: <HomeOutlined sx={{ fontSize: 16 }} />, bg: "rgba(255,255,255,0.18)" },
};

/**
 * Top third of the property dashboard (October 2026, Task 1): the home's
 * photo full-width, faded to dark at the sides so the address reads, with the
 * claim badge and "View Photos". Image preference — owner's featured photo →
 * Google satellite → Street View — is decided by the caller; this component
 * just falls through the list when one fails to load, and shows a branded
 * gradient if none do.
 */
const PropertyHeader: React.FC<Props> = ({
  line1,
  line2,
  imageCandidates,
  photoCount,
  onViewPhotos,
  claimState,
  onClaim,
  wishlist,
  aside,
}) => {
  const [imageIndex, setImageIndex] = useState(0);
  const candidatesKey = imageCandidates.join("|");
  useEffect(() => setImageIndex(0), [candidatesKey]);
  const src = imageCandidates[imageIndex];
  const badge = BADGES[claimState];

  return (
    <Box
      component="section"
      aria-label="Property header"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: { xs: "16px", md: "22px" },
        minHeight: { xs: 300, sm: 320, md: 340 },
        bgcolor: "#0d3536",
        backgroundImage: "radial-gradient(120% 140% at 80% 0%, #1d6a6b 0%, #0d3536 55%, #072223 100%)",
        display: "flex",
      }}
      data-testid="property-header"
    >
      {src && (
        <Box
          component="img"
          src={src}
          alt=""
          onError={() => setImageIndex((i) => i + 1)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          data-testid="property-header-image"
        />
      )}
      {/* Fade to dark on the sides (Asher) + a floor so the text always reads. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: {
            xs: "linear-gradient(0deg, rgba(4,28,29,0.92) 0%, rgba(4,28,29,0.55) 55%, rgba(4,28,29,0.25) 100%)",
            md: "linear-gradient(90deg, rgba(4,28,29,0.9) 0%, rgba(4,28,29,0.62) 30%, rgba(4,28,29,0.12) 52%, rgba(4,28,29,0.12) 60%, rgba(4,28,29,0.7) 100%), linear-gradient(0deg, rgba(4,28,29,0.45) 0%, rgba(4,28,29,0) 45%)",
          },
        }}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-end" }}
        spacing={4}
        sx={{ position: "relative", width: "100%", p: { xs: 5, sm: 7, md: 9 }, mt: "auto" }}
      >
        <Box sx={{ minWidth: 0, color: "#fff" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              display: "inline-flex",
              bgcolor: badge.bg,
              px: 2.5,
              py: 1,
              borderRadius: 50,
              mb: 3,
            }}
            data-testid="claim-badge"
          >
            {badge.icon}
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.2 }}>{badge.label}</Typography>
          </Stack>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: 28, sm: 34, md: 40 },
              lineHeight: 1.1,
              textShadow: "0 2px 12px rgba(0,0,0,0.35)",
              wordBreak: "break-word",
            }}
          >
            {line1}
          </Typography>
          <Typography
            sx={{ fontSize: { xs: 16, md: 20 }, mt: 1, opacity: 0.92, textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
          >
            {line2}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 5, flexWrap: "wrap", rowGap: 2 }}>
            <Button
              onClick={onViewPhotos}
              startIcon={<PhotoLibraryOutlined />}
              sx={{
                bgcolor: "#fff",
                color: DASH.teal,
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                "&:hover": { bgcolor: "#f1f5f5" },
              }}
              data-testid="view-photos-btn"
            >
              View Photos{photoCount > 0 ? ` (${photoCount})` : ""}
            </Button>
            {(claimState === "unclaimed" || claimState === "claimed") && (
              <Button
                onClick={onClaim}
                variant="outlined"
                sx={{
                  // On phones the claim card ("Is this your home?" / "This home
                  // has been claimed") sits right under the header; one claim
                  // button is enough.
                  display: aside ? { xs: "none", md: "inline-flex" } : "inline-flex",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.7)",
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                }}
                data-testid="header-claim-btn"
              >
                I own this home
              </Button>
            )}
            {wishlist && (
              <Tooltip title={wishlist.active ? "Remove from wishlist" : "Save to wishlist"}>
                <IconButton
                  onClick={wishlist.onToggle}
                  aria-label={wishlist.active ? "Remove from wishlist" : "Save to wishlist"}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.35)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
                  }}
                  data-testid="wishlist-btn"
                >
                  {wishlist.active ? <Favorite sx={{ color: "#ff8a80" }} /> : <FavoriteBorder />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {aside && <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}>{aside}</Box>}
      </Stack>
    </Box>
  );
};

export default PropertyHeader;
